'use client'

import type { MegaMenuType } from '@/frontend-types'
import { EASE_OUT_CUBIC, MENU_DURATION } from '@/helpers/timing'
import type { Variants } from 'motion/react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, type FC } from 'react'
import { ResizingContainer } from '../atoms/ResizingContainer'
import type { MegaMenuSideProps } from '../molecules/MegaMenuSide'
import { Search } from '../molecules/Search'
import { Header } from './Header'
import { MegaMenu } from './MegaMenu'

export type NavState = 'closed' | 'menu' | 'search'

const bgVariants: Variants = {
	hidden: { opacity: 0 },
	visible: { opacity: 0.9 },
}

const overlayVariants: Variants = {
	hidden: { clipPath: 'inset(0 0 100% 0)' },
	visible: { clipPath: 'inset(0 0 0 0)' },
}

export type NavOverlayProps = {
	state: NavState
	megaMenu?: MegaMenuType
	megaMenuSide?: MegaMenuSideProps
	onNavigate: (state: NavState) => void
}

export const NavOverlay: FC<NavOverlayProps> = ({ state, megaMenu, megaMenuSide, onNavigate }) => {
	const handleNavigate = useCallback(
		(next: NavState) => {
			onNavigate(next === state ? 'closed' : next)
		},
		[state, onNavigate],
	)

	return (
		<div className="fixed top-0 left-0 w-full h-full z-100">
			{/* Background */}
			<motion.div
				className="absolute inset-0 bg-blue-shade-70"
				variants={bgVariants}
				initial="hidden"
				animate="visible"
				exit="hidden"
				transition={{ ease: 'linear', duration: MENU_DURATION * 0.75 }}
			/>
			{/* Content */}
			<motion.div
				className="relative flex flex-col text-white max-h-full overflow-y-auto bg-blue"
				variants={overlayVariants}
				initial="hidden"
				animate="visible"
				exit="hidden"
				transition={{ ease: EASE_OUT_CUBIC, duration: MENU_DURATION }}
			>
				{/* Header */}
				<div className="shrink-0 sticky top-0 z-10">
					<Header theme="blue" state={state} onNavigate={handleNavigate} />
				</div>
				<ResizingContainer>
					<AnimatePresence mode="popLayout">
						<motion.div
							key={state}
							variants={{
								enter: {
									opacity: 0,
									y: '-2rem',
									transition: { ease: EASE_OUT_CUBIC, duration: MENU_DURATION * 2 },
								},
								visible: {
									opacity: 1,
									y: '0rem',
									transition: { ease: EASE_OUT_CUBIC, duration: MENU_DURATION * 2 },
								},
								exit: {
									opacity: 0,
									y: '0rem',
									transition: {
										ease: EASE_OUT_CUBIC,
										duration: MENU_DURATION * 0.5,
									},
								},
							}}
							initial="enter"
							animate="visible"
							exit="exit"
						>
							{state === 'menu' ? (
								<MegaMenu megaMenu={megaMenu} megaMenuSide={megaMenuSide} />
							) : (
								<Search />
							)}
						</motion.div>
					</AnimatePresence>
				</ResizingContainer>
			</motion.div>
		</div>
	)
}
